import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const LoginPage = () => {
  const { login } = useAuth();
  const [mode, setMode] = useState("choice"); // choice, otp_email, otp_verify
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Login page is pre-auth — fetch the agency-wide brand via a public-ish path.
  // We hit /api/branding/agency without a token; backend returns 401 → fallback.
  const [brand, setBrand] = useState({ logo_base64: "", portal_title: "Digital Impressions", login_tagline: "", accent_color: "#FFEB00" });
  useEffect(() => {
    // Try fetching agency brand with whatever session_token is in localStorage
    // (if a user previously logged in, this still works). Otherwise fall back.
    axios.get(`${API}/branding/agency`).then(r => {
      if (r.data?.branding) {
        const b = { ...brand, ...r.data.branding };
        setBrand(b);
        if (b.accent_color) {
          document.documentElement.style.setProperty("--accent", b.accent_color);
        }
        if (b.portal_title) document.title = b.portal_title;
      }
    }).catch(() => { /* unauth → defaults are fine */ });
  }, []);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const sendOtp = async () => {
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/auth/otp/send`, { email });
      setMode("otp_verify");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send OTP");
    } finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/auth/otp/verify`, { email, otp });
      if (res.data.success) {
        login(res.data.user, res.data.session_token);
        window.location.href = "/";
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid OTP");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center" data-testid="login-page">
      <div className="w-full max-w-md p-8">
        <div className="mb-10 text-center">
          {brand.logo_base64 ? (
            <img src={brand.logo_base64} alt={brand.portal_title} className="max-h-20 mx-auto" data-testid="login-brand-logo" />
          ) : (
            <img src="/digital-impressions-logo.png" alt={brand.portal_title || "Digital Impressions"} className="h-20 mx-auto" data-testid="login-brand-logo" />
          )}
          {brand.login_tagline && (
            <p className="mt-4 text-sm text-[#A1A1AA]" data-testid="login-tagline">{brand.login_tagline}</p>
          )}
        </div>

        <div className="card-flat p-8">
          {mode === "choice" && (
            <div className="space-y-4">
              <button onClick={handleGoogleLogin} className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-white text-[#0A0A0A] font-medium rounded hover:bg-gray-100 transition-colors" data-testid="google-login-btn">
                <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                Sign in with Google
              </button>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-[#2A2A2A]" />
                <span className="text-xs text-[#71717A]">or</span>
                <div className="flex-1 h-px bg-[#2A2A2A]" />
              </div>

              <button onClick={() => setMode("otp_email")} className="w-full py-3 px-4 border border-[#2A2A2A] text-sm font-medium hover:border-[#FFEB00] transition-colors" data-testid="otp-login-btn">
                Sign in with Email OTP
              </button>
            </div>
          )}

          {mode === "otp_email" && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Email Login</h2>
              <div>
                <label className="form-label">Email Address</label>
                <input type="email" className="input-default w-full" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com" autoFocus data-testid="otp-email-input" />
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button onClick={sendOtp} disabled={loading || !email} className="btn-primary w-full py-3" data-testid="send-otp-btn">
                {loading ? "Sending..." : "Send OTP"}
              </button>
              <button onClick={() => { setMode("choice"); setError(""); }} className="btn-ghost w-full text-sm">Back</button>
            </div>
          )}

          {mode === "otp_verify" && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Enter OTP</h2>
              <p className="text-sm text-[#71717A]">Code sent to {email}</p>
              <div>
                <input type="text" className="input-default w-full text-center text-2xl tracking-[0.5em] font-mono" value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6} autoFocus
                  placeholder="000000" data-testid="otp-code-input" />
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button onClick={verifyOtp} disabled={loading || otp.length !== 6} className="btn-primary w-full py-3" data-testid="verify-otp-btn">
                {loading ? "Verifying..." : "Verify & Login"}
              </button>
              <button onClick={() => { setMode("otp_email"); setOtp(""); setError(""); }} className="btn-ghost w-full text-sm">Back</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
